/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Attachment } from '@src/app/model/attachment';

import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { MatDialog } from '@angular/material/dialog';
import { AttachmentService } from '@src/app/shared/form/file/attachment.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { Omc } from '@src/app/model/Omc';
import { User } from '@src/app/model/user';
import { Router } from '@angular/router';
import moment from 'moment';
import { AttachmentControl, AttachmentField, FileOpts } from '@src/app/shared/field/AttachmentField';
import { ControlOn } from '@src/app/shared/form/app-form-control';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { ByteCountPipe } from '@src/app/shared/pipes/byte-count.pipe';
import { FromNowPipe } from '@src/app/shared/pipes/from-now.pipe';
import { FileUploadComponent } from '@src/app/shared/form/file/file-upload/file-upload.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';
import { FileTypeIconComponent } from '@src/app/shared/file-type-icon/file-type-icon.component';

@Component({
    selector: 'app-attachment-list',
    templateUrl: './attachment-list.component.html',
    styleUrls: ['./attachment-list.component.scss'],
    imports: [CdkDropList, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule,
        MatIconModule, MatTooltipModule, MatButtonModule, FileUploadComponent, FromNowPipe, ByteCountPipe, NgClass,
        FileTypeIconComponent]
})
export class AttachmentListComponent implements FormControlInterface {
    protected cds = inject(ConfirmDialogService);
    protected dialog = inject(MatDialog);
    protected attachSvc = inject(AttachmentService);
    private router = inject(Router);


    @Input() label: string;
    @Input() control: AttachmentControl;
    @Input() on: ControlOn = 'form';
    @Input() attachToId = 0;

    moment = moment;

    currentUser: User;
    omcs: Omc[] = [];

    constructor() {
        const agentDataSvc = inject(AgentDataService);
        const currentUserSvc = inject(CurrentUserService);


        currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe(user => {
            this.currentUser = user;
            if (this.currentUser.inAgency) {
                agentDataSvc.getOmcList().subscribe(omcs => {
                    this.omcs = (omcs as Omc[]).filter(o => o.omcTeamId);
                });
            }
        });

    }

    downloadFile($event, a: Attachment) {
        $event.preventDefault();

        const svc = this.control.field.fileOpts.service; // as PurchaseService);
        Attachment.download(a, svc);
    }

    getAttachTo() {
        if (this.attachToId) {
            return this.attachToId;
        }
    }

    deleteAttachment(attachment: Attachment) {
        Attachment.deleteAttachment(attachment, this.cds, this.control.field);
    }

    invoiceNow(o: Attachment) {
        this.router.navigate([PurchasePageNavRoute.newUrl, { attach: o.id }]);
    }

    editAttachment(attachment: Attachment) {
        Attachment.editAttachment(attachment, this.dialog, this.control.field, this.attachSvc, this.omcs);
    }

    public static make(label: string, value: string, fileOpts: FileOpts, options: Partial<AttachmentField> = {}) {
        const field: AttachmentField = new AttachmentField({ label, value }, options);

        field.fileOpts = fileOpts;
        field.fileOpts.deferredFiles = [];
        field.formControlFactory = AttachmentListComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: AttachmentControl, on: ControlOn) {
        const componentRef = vcr.createComponent(AttachmentListComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    drop(event: CdkDragDrop<string[]>) {

        if (event.previousContainer === event.container) {
            console.warn('DROPPED ON SAME CONTAINER');
            //moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            /*
            transferArrayItem(event.previousContainer.data,
                              event.container.data,
                              event.previousIndex,
                              event.currentIndex); */

            const prevCtl = event.item.data.ctl;
            const prevArray = prevCtl.value;
            const prevLength = prevCtl.value.length;

            const item = event.item.data.attachment;
            const prevIndex = prevArray.indexOf(item)
            if (prevIndex > 0) {
                prevArray.splice(prevIndex, 1);
            } else {
                console.error('Could not remove attachment form list', { item, prevIndex, arry: prevArray })
            }

            this.control.value.push(item);

            // Fire the changeDetection
            this.control.setValue(this.control.value);
            prevCtl.setValue(prevArray);
            if (prevLength - 1 !== prevCtl.value.length) {
                console.error(`How did we take more than one out of list!!! We had ${prevLength} now there is ${prevCtl.value.length}`);
            }

        }

        console.warn({ f: this.control.field.name, event, v: this.control.value });
    }
}
